import Timeout from 'await-timeout';
import cloneDeep from 'lodash.clonedeep';
import {getUserData} from "./API";
import { logSessionInfo } from "./logger";
import { cleanWord, checkForResponseMatch } from './NLP';
import { getRandomArrayElement } from '../library';
import { COPY_STRINGS } from 'Constants/translatedCopy';
import { interventionListenConfigs, defaultInterventionListenConfig, targetTypes, listeningPatterTypes, defaultListeningPatter } from 'Constants/interventionListeningConstants';
import { checkPhonemeMatch } from './amirabet';

const minListenTime = 1000;
const maxListenTime = 5000;
let lastPhonemeTime = new Date();

let listener = {
  targetWord: null,
  targetMetadata: null,
  minTimeElapsed: false,
  maxTimeElapsed: false,
  minTimeout: new Timeout(),
  maxTimeout: new Timeout(),
  transcript: null,
  matches: {},
  retries: 0,
};

export function listen(args){
  const {speech, targetWord, targetMetadata, closeText, interventionType, patterOverride=[]} = args;
  listener.targetWord = targetWord;
  listener.targetMetadata = targetMetadata;

  let config = defaultInterventionListenConfig;
  if (interventionType) config = interventionListenConfigs[interventionType]
  else {
    // if interventionType is not specified, use patterOverride
    // patterOverride contains an array of patterTypes to "override"
    // allowing patter configuration at the listen request level
    const patter = {...defaultListeningPatter}
    for (const patterType of patterOverride) patter[patterType] = listeningPatterTypes.none;
    config = {...defaultInterventionListenConfig, patter}
  }
  listener.config = config;

  if (closeText) {
    speech.speak({textToSpeak: closeText, onEnd: startListening.bind(null, args)});
  }else {
    startListening(args);
  }
}

function startListening(args){
  const {speech, phoneme, audioDestination, callbackOverride, useComprehension, callback} = args;
  logSessionInfo("intervention_startListen", {args: args, listener: listener});

  speech.setAudioDestination(audioDestination);
  speech.requestTranscript(callbackOverride ? callbackOverride.bind(null, args) : listenCallback.bind(null, args), phoneme, useComprehension, callback);
  listener.isActive = true;

  listener.minTimeElapsed = false;
  listener.minTimeout.set(minListenTime).then(() => {
    listener.minTimeElapsed = true;
    callbackOverride ? callbackOverride(args) : handleMatches(args);
  });

  listener.maxTimeElapsed = false;
  listener.maxTimeout.set(maxListenTime).then(() => {
    listener.maxTimeElapsed = true;
    callbackOverride ? callbackOverride(args) : handleMatches(args);
  });
}

function listenCallback(args, transcript) {
  if (args.phoneme) {
    // add short cooldown when listening for phonemes
    if (new Date() - lastPhonemeTime < 400) return;
    lastPhonemeTime = new Date();
  }

  if (transcript) {
    listener.transcript = transcript;
    if(args.phoneme){
      args.phonemeMatch = checkPhonemeMatch(cleanWord(listener.targetWord), listener.transcript);
    }else{
      listener.matches = checkForResponseMatch(cleanWord(listener.targetWord), listener.transcript);
    }
  }

  handleMatches(args);
}

function handleMatches(args){
  const targetMatch = ((listener.matches.targetMatches >= listener.config.targetMatchesGoal) || args.phonemeMatch) && listener.minTimeElapsed;

  if (targetMatch || listener.matches.moveOn || listener.maxTimeElapsed) {
    const {speech, endOfPhrase} = args;
    const {patter} = listener.config;

    //clear timeouts and stop listening
    listener.minTimeout.clear();
    listener.maxTimeout.clear();
    speech.cancelTranscriptRequest(!endOfPhrase);
    listener.isActive = false;

    let patterType;

    if(getUserData().locale === 'es-mx'){
      //AE-9121: use neutral patter for spanish interventions temporarily until interim transcripts are wired up
      patterType = args.phoneme ? listeningPatterTypes.none : listeningPatterTypes.giveAnswer;
    } else {
      if (targetMatch) { //response contains an exact match of the intervention word
        patterType = patter.match;
      } else if (listener.matches.moveOn) { //response matches a move on keyword ("skip", "don't know", "move on", etc.)
        patterType = patter.skip;
      } else if (listener.maxTimeElapsed) {
        if (listener.transcript) { //response does not contain any matches because reader is struggling or substituting words.
          patterType = patter.struggle;
        } else { //response was too quiet or silent
          patterType = patter.silence;
          if(patterType === listeningPatterTypes.didntHear) {
            if (!args.noRetries && listener.retries < 1) {
              listener.retries++;
              listen({ ...args, closeText: getRandomArrayElement(COPY_STRINGS.intervention_strings.listening[patter.silence])});
              return;
            } else {
              patterType = listeningPatterTypes.giveAnswer;
            }
          }
        }
      }
    }

    if(args.noPatter) {
      listener.status = patterType;
      patterType = listeningPatterTypes.none;
    }

    if(patterType && patterType !== listeningPatterTypes.none){
      speakPatter(patterType, listener.targetWord, args);
    } else {
      endListen(args);
    }
  }
}

function speakPatter(patterType, targetWord, args){
  const {speech} = args;

  let patter = getRandomArrayElement(COPY_STRINGS.intervention_strings.listening[patterType]);
  if(patterType === listeningPatterTypes.giveAnswer){
    if(getUserData().bilingualMode){
      speech.speak({textToSpeak: patter});
      speech.speak({textToSpeak: "<prosody rate=\"slow\"> " + targetWord + " </prosody>", skipTranslation: true, onEnd: endListen.bind(null, args)}, undefined, undefined, true);
    }else{
      speech.speak({textToSpeak: `${patter} "${targetWord}".`, onEnd: endListen.bind(null, args) });
    }
  }else{
    speech.speak({textToSpeak: patter, onEnd: endListen.bind(null, args) });
  }
}

function endListen(args){
  const {endOfPhrase, speech, callback} = args;
  logSessionInfo("intervention_endListen", {listener: listener});

  if(endOfPhrase){
    speech.resetTranscript();
  }

  const listenResults = cloneDeep(listener);
  resetListener();
  callback(listenResults);
}

export function abortListen(speech, endOfPhrase){
  logSessionInfo("intervention_abortListen", {listener: listener});
  if(listener.isActive){
    speech.cancelTranscriptRequest(!endOfPhrase);
    resetListener();

    if(endOfPhrase){
      speech.resetTranscript();
    }
  }
}

function resetListener(){
  listener.isActive = false;
  listener.minTimeout.clear();
  listener.maxTimeout.clear();
  listener.minTimeElapsed = false;
  listener.maxTimeElapsed = false;
  listener.transcript = null;
  listener.matches = {};
  listener.retries = 0;
  listener.status = null;
}